import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  CASH_NOTES,
  CASH_SMALL_NOTES,
  cashSuggestions,
  describeSteps,
  noteCounts,
  tallySteps,
  type TenderStep,
} from '@/lib/cashTender'

export interface CashQuickTenderProps {
  amountDue: number
  received: string
  onReceivedChange: (value: string) => void
  /** Split-bill guest card: shorter buttons, same layout. */
  compact?: boolean
  disabled?: boolean
}

/**
 * F-FE-N209 — button-only cash entry, so a touch counter never needs the OS
 * keyboard. Top row SETS the amount (Exact + round-ups of the bill); the "+"
 * rows ADD a note to it, with a ×N badge per note and a running breakdown.
 * The text field above stays the authority — typing into it simply detaches
 * the tally, and the next note tap starts from whatever was typed.
 */
export function CashQuickTender({
  amountDue,
  received,
  onReceivedChange,
  compact = false,
  disabled = false,
}: CashQuickTenderProps) {
  const [steps, setSteps] = useState<TenderStep[]>([])

  const receivedNum = parseFloat(received || '0')
  const current = Number.isFinite(receivedNum) ? receivedNum : 0
  // The tally only describes the field while they agree; a typed value, or
  // the parent clearing the field after a payment, detaches it.
  const synced = steps.length > 0 && Math.abs(tallySteps(steps) - current) < 0.005
  const live = synced ? steps : []
  const counts = noteCounts(live)

  const apply = (next: TenderStep[]) => {
    setSteps(next)
    onReceivedChange(next.length ? tallySteps(next).toFixed(2) : '')
  }
  const setAmount = (amount: number) => apply([{ kind: 'set', amount }])
  const addNote = (note: number) => {
    const base: TenderStep[] = synced ? steps : current > 0 ? [{ kind: 'set', amount: current }] : []
    apply([...base, { kind: 'add', note }])
  }

  const btnH = compact ? 'h-9 text-sm' : 'h-11 text-base'
  const smallH = compact ? 'h-8 text-xs' : 'h-9 text-sm'

  const noteButton = (note: number, sizeClass: string) => {
    const n = counts.get(note) ?? 0
    return (
      <Button
        key={note}
        type="button"
        variant="outline"
        disabled={disabled}
        className={cn('relative font-semibold tabular-nums', sizeClass, n > 0 && 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40')}
        onClick={() => addNote(note)}
      >
        +{note.toLocaleString()}
        {n > 1 && (
          <span className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full bg-emerald-600 px-1.5 text-[11px] font-bold leading-5 text-white">
            ×{n}
          </span>
        )}
      </Button>
    )
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(4.5rem,1fr))] gap-2">
        <Button
          type="button"
          disabled={disabled || !(amountDue > 0)}
          className={cn('font-semibold bg-emerald-600 text-white hover:bg-emerald-700', btnH)}
          onClick={() => setAmount(amountDue)}
        >
          Exact
        </Button>
        {cashSuggestions(amountDue).map((v) => (
          <Button
            key={v}
            type="button"
            disabled={disabled}
            className={cn('font-semibold tabular-nums bg-emerald-600 text-white hover:bg-emerald-700', btnH)}
            onClick={() => setAmount(v)}
          >
            {v.toLocaleString()}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-2">{CASH_NOTES.map((n) => noteButton(n, btnH))}</div>

      <div className="grid grid-cols-4 gap-2">
        {CASH_SMALL_NOTES.map((n) => noteButton(n, smallH))}
        <Button
          type="button"
          variant="ghost"
          disabled={disabled || !received}
          className={cn('font-medium', smallH)}
          onClick={() => apply([])}
        >
          Clear
        </Button>
      </div>

      {live.length > 0 && (
        <div className="flex items-center justify-between gap-2 text-xs text-emerald-900/80 dark:text-emerald-100/80">
          <span className="truncate tabular-nums">{describeSteps(live)}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-7 px-2 text-xs"
            onClick={() => apply(steps.slice(0, -1))}
          >
            Undo
          </Button>
        </div>
      )}
    </div>
  )
}
